import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const TOTAL_PAGES = 12;
const pages = Array.from({ length: TOTAL_PAGES }, (_, i) => `/speisekarte/seite-${i + 1}.png`);
const PDF_URL = '/speisekarte/Speisekarte-Alfa.pdf';

export default function MenuViewer({ open, onClose }) {
  const [active, setActive] = useState(0);
  const stageRef = useRef(null);
  const thumbsRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setActive((a) => Math.min(a + 1, TOTAL_PAGES - 1));
      if (e.key === 'ArrowLeft') setActive((a) => Math.max(a - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (stageRef.current) stageRef.current.scrollTop = 0;
    const el = thumbsRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [active]);

  if (!open) return null;

  const prev = () => setActive((a) => Math.max(a - 1, 0));
  const next = () => setActive((a) => Math.min(a + 1, TOTAL_PAGES - 1));

  return (
    <div className="fixed inset-0 z-[100] bg-[#0c0a08]/97 backdrop-blur-sm flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-[rgba(224,180,159,0.15)] shrink-0">
        <div className="flex flex-col leading-none">
          <span className="font-serif-display text-xl tracking-[0.2em] text-[#f2ebe3] pl-[0.2em]">SPEISEKARTE</span>
          <span className="eyebrow text-[0.58rem] mt-1.5 pl-1">Restaurant Alfa · Heideck</span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={PDF_URL}
            download
            className="hidden sm:flex items-center gap-2 rounded-full border border-[rgba(224,180,159,0.4)] text-[#f2ebe3] px-4 py-2 text-sm hover:bg-[#e0b49f] hover:text-[#1a1410] hover:border-[#e0b49f] transition-all duration-300"
          >
            <Download size={15} /> PDF laden
          </a>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-10 w-10 rounded-full border border-[rgba(224,180,159,0.4)] text-[#f2ebe3] hover:bg-[#e0b49f] hover:text-[#1a1410] hover:border-[#e0b49f] transition-all duration-300"
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Main stage */}
      <div className="flex-1 min-h-0 flex">
        {/* Prev button */}
        <button
          onClick={prev}
          disabled={active === 0}
          className="hidden md:flex items-center justify-center w-16 shrink-0 text-[#e0b49f] disabled:opacity-25 hover:bg-[rgba(224,180,159,0.06)] transition-colors"
          aria-label="Vorherige Seite"
        >
          <ChevronLeft size={38} />
        </button>

        {/* Page image */}
        <div ref={stageRef} className="flex-1 overflow-auto flex items-start justify-center py-6 px-3 md:px-6">
          <img
            src={pages[active]}
            alt={`Speisekarte Seite ${active + 1}`}
            className="w-auto max-w-full md:max-w-[720px] rounded-lg shadow-2xl shadow-black/60"
          />
        </div>

        {/* Next button */}
        <button
          onClick={next}
          disabled={active === TOTAL_PAGES - 1}
          className="hidden md:flex items-center justify-center w-16 shrink-0 text-[#e0b49f] disabled:opacity-25 hover:bg-[rgba(224,180,159,0.06)] transition-colors"
          aria-label="Nächste Seite"
        >
          <ChevronRight size={38} />
        </button>
      </div>

      {/* Mobile nav + counter */}
      <div className="flex items-center justify-center gap-6 py-3 md:hidden">
        <button onClick={prev} disabled={active === 0} className="text-[#e0b49f] disabled:opacity-25">
          <ChevronLeft size={30} />
        </button>
        <span className="text-[#c3b7aa] text-sm tracking-widest">{active + 1} / {TOTAL_PAGES}</span>
        <button onClick={next} disabled={active === TOTAL_PAGES - 1} className="text-[#e0b49f] disabled:opacity-25">
          <ChevronRight size={30} />
        </button>
      </div>

      {/* Thumbnails */}
      <div className="shrink-0 border-t border-[rgba(224,180,159,0.15)] bg-[#0c0a08]">
        <div ref={thumbsRef} className="flex items-center gap-3 overflow-x-auto px-5 md:px-8 py-4">
          {pages.map((src, i) => (
            <button
              key={i}
              data-idx={i}
              onClick={() => setActive(i)}
              className={`relative shrink-0 rounded-md overflow-hidden border-2 transition-all duration-300 ${
                active === i ? 'border-[#e0b49f] scale-105' : 'border-transparent opacity-55 hover:opacity-100'
              }`}
            >
              <img src={src} alt={`Seite ${i + 1}`} className="h-20 w-auto" />
              <span className="absolute bottom-0 right-0 bg-[#0c0a08]/80 text-[#f2ebe3] text-[10px] px-1.5 py-0.5">{i + 1}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
